"use client";

import { useEffect, useRef } from "react";

type RumErrorLogger =
  | ((error: unknown, context?: Record<string, unknown>) => void)
  | undefined;

declare global {
  interface Window {
    DD_RUM?: {
      addError?: RumErrorLogger;
    };
    DD_LOGS?: {
      logger?: {
        error?: (message: string, context?: Record<string, unknown>) => void;
      };
    };
  }
}

const MAX_EVENTS = 50;
const WINDOW_MS = 60_000;

function getRecentResources() {
  if (typeof performance === "undefined") return [];
  const entries = performance.getEntriesByType(
    "resource",
  ) as PerformanceEntry[];
  return entries.slice(-5).map((entry) => {
    const timing = entry as PerformanceResourceTiming;
    return {
      name: timing.name,
      initiatorType: timing.initiatorType,
    };
  });
}

function logRumEvent(message: string, payload: Record<string, unknown>) {
  const { DD_RUM, DD_LOGS } = window;
  if (DD_RUM?.addError) {
    DD_RUM.addError(message, payload);
    return;
  }
  if (DD_LOGS?.logger?.error) {
    DD_LOGS.logger.error(message, payload);
    return;
  }
}

export function ScriptErrorLogger() {
  const timestamps = useRef<number[]>([]);

  const shouldLog = () => {
    const now = Date.now();
    timestamps.current = timestamps.current.filter((t) => now - t < WINDOW_MS);
    if (timestamps.current.length >= MAX_EVENTS) return false;
    timestamps.current.push(now);
    return true;
  };

  useEffect(() => {
    const onError = (ev: ErrorEvent) => {
      if (!shouldLog()) return;
      logRumEvent(ev.message || "Script error", {
        kind: "window.error",
        message: ev.message,
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        stack: ev.error?.stack,
        href: window.location.href,
        referrer: document.referrer || undefined,
        ua: navigator.userAgent,
        isScriptError: ev.message === "Script error.",
        lastResources: getRecentResources(),
      });
    };

    const onUnhandledRejection = (ev: PromiseRejectionEvent) => {
      if (!shouldLog()) return;
      logRumEvent("unhandledrejection", {
        kind: "window.unhandledrejection",
        reason: String(ev.reason),
        stack: ev.reason instanceof Error ? ev.reason.stack : undefined,
        href: window.location.href,
        referrer: document.referrer || undefined,
        ua: navigator.userAgent,
      });
    };

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onUnhandledRejection, true);

    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener(
        "unhandledrejection",
        onUnhandledRejection,
        true,
      );
    };
  }, []);

  return null;
}
